// Production loader for the stash-scoped store.
//
// One useEffect per (source × homeBase) combo: load the initial snapshot,
// hand it to `store.initialize`, then open the live `subscribe(...)` and feed
// every page into `store.applyChanges`. The effect's cleanup tears down the
// active subscription so re-mounts and prop changes never leak listeners.

import { useEffect, useRef } from 'react';
import { useStashScopedStore } from '../context';
import type { StashUpdatesSource } from './StashUpdatesSource';
import { currentPageLoginPath } from '../../../lib/login-redirect';

export interface StashBoardDataLoaderProps {
  source: StashUpdatesSource;
}

// Indirection: production redirects via `window.location.assign` (hard nav so
// stale auth state is fully reset). Held in a module-level binding so tests
// can swap it without fighting jsdom's non-configurable `window.location`.
let redirectToLogin: () => void = (): void => {
  window.location.assign(currentPageLoginPath());
};

/** Test-only seam: replace the auth-bounce redirect. */
export function __setRedirectToLoginForTests(fn: () => void): void {
  redirectToLogin = fn;
}

export function StashBoardDataLoader({ source }: StashBoardDataLoaderProps): null {
  const initialize = useStashScopedStore(s => s.initialize);
  const applyChanges = useStashScopedStore(s => s.applyChanges);
  const homeBase = useStashScopedStore(s => s.homeBase);
  // Cursor kept out of React state — it changes per delta and shouldn't trigger
  // re-renders. The latest value is read by the subscribe `onChanges` closure.
  const cursorRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: () => void = () => {};

    (async () => {
      try {
        const snap = await source.loadInitial({ homeBase: homeBase ?? null });
        if (cancelled) return;

        initialize(snap.cards, snap.vendors, snap.stashers, snap.categories);
        cursorRef.current = snap.cursor;

        unsubscribe = source.subscribe({
          cursor: cursorRef.current,
          // vendorIds intentionally omitted → default (all-active) scope.
          // A future narrowing UI would re-source via a different provider.
          onChanges: (changes, nextCursor) => {
            applyChanges(changes);
            cursorRef.current = nextCursor;
          },
        });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        if (/not authenticated/i.test(message)) {
          // Auth expired or the user isn't signed in — bounce to login with a
          // hard navigation so any stale in-memory auth state is fully reset.
          // Mirrors the redirect in ChatPage.
          redirectToLogin();
          return;
        }
        // Non-auth failures (network, 5xx, parse) — log and stay put so it
        // doesn't surface as an unhandled rejection. The store stays empty;
        // a future retry/error-state UI could surface this to the user.
        // eslint-disable-next-line no-console
        console.error('[StashBoardDataLoader] loadInitial/subscribe failed:', err);
      }
    })();

    return (): void => {
      cancelled = true;
      unsubscribe();
    };
  }, [source, homeBase, initialize, applyChanges]);

  return null;
}
